import { useEffect } from 'react';
import { usePlayer as useReactPlayer } from '@landr/react-player';
import { StreamEntityType } from 'types/enums';
import { selectSample, demoPlaybackStarted } from 'helpers/dawPlugin';
import useStream from './hooks/useStream';
import usePlayerActions from './hooks/usePlayerActions';
import useError from './hooks/useError';
import useKeyboardEvents from './hooks/useKeyboardEvents';
import useExpiration from './hooks/useExpiration';
import { UsePlayerType } from './types';

// Stream url expired after 5 minutes - we want to refetch a new stream url before that
const STREAM_URL_EXPIRATION = 4 * 60 * 1000;

const usePlayer = ({
    streamEntityType,
    id,
    ref,
}: {
    streamEntityType: StreamEntityType;
    id?: string;
    ref?: React.RefObject<Element>;
}): UsePlayerType => {
    const {
        currentTime,
        error: reactPlayerError,
        loading: reactPlayerLoading,
        playing,
        totalTime,
        seek,
        setLoading,
        setPlaying,
        setSources,
    } = useReactPlayer(id || null);

    const { onError } = useError({
        setSources,
        setLoading,
        setPlaying,
    });

    useEffect(() => {
        if (reactPlayerError) {
            onError({
                error: reactPlayerError,
                errorMessage: 'React Player internal error',
            });
        }
    }, [reactPlayerError, onError]);

    const { getIsExpired, updateExpirationDate } = useExpiration({
        expiration: STREAM_URL_EXPIRATION,
    });

    const { playStream } = useStream({
        seek,
        setPlaying,
        id,
        streamEntityType,
        setSources,
        onError,
        getIsExpired,
        updateExpirationDate,
        setLoading,
    });

    const { playAnotherFragment, playFromBeginning, togglePlaying } =
        usePlayerActions({
            id,
            playing,
            streamEntityType,
            totalTime,
            demoPlaybackStarted,
            selectSample,
            setPlaying,
            playStream,
        });

    useKeyboardEvents({
        id,
        ref,
        streamEntityType,
        playAnotherFragment,
        playFromBeginning,
        togglePlaying,
    });

    const isLoading = reactPlayerLoading;

    return {
        currentTime,
        loading: isLoading,
        playing,
        totalTime,
        togglePlaying,
        playFromBeginning,
        seek,
    };
};

export default usePlayer;
